import {Pagination} from '@store/common/pagination.model';
import {OrganisationLight, OrganisationUnitLight} from '@store/common/organisation.model';
import {ServiceKind, ServiceKindLight, ServiceKindTypeEnum, UnitOfMeasure,} from '@store/settings/serviceKinds';
import {Waste} from '@store/settings/wasteFamilies';
import {Container} from '@store/settings/containers';
import {DocumentInfo} from '@store/files';
import I18n from '@utils/i18n.utils';
import {UserLight} from '@store/settings/users';
import {Colors} from '@constants/colors.constants';
import {SelectItem} from '@components/inputs/select/Select.component';
import {alpha} from '@mui/material';
import {ClientsService} from '@store/clients/services';
import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';
import {ClientsUnit} from '@store/clients/units';

export enum ExecutionFilterDate {
    PLANNED = 'PLANNED',
    CREATED = 'CREATED',
}

export namespace ExecutionFilterDate {
    export const selectItems = (): SelectItem[] => [
        {label: I18n.t('global.executionFilterDate.PLANNED'), value: ExecutionFilterDate.PLANNED},
        {label: I18n.t('global.executionFilterDate.CREATED'), value: ExecutionFilterDate.CREATED},
    ];
}


export enum ExecutionStatus {
  CREATED = 'CREATED',
  PLANNED = 'PLANNED_OR_ACCEPTED',
  EXECUTED = 'EXECUTED',
}

export namespace ExecutionStatus {
  export const selectItems = (): SelectItem[] => [
    {
      label: I18n.t('global.statusEnum.created'),
      value: ExecutionStatus.CREATED,
      backgroundColor: alpha(Colors.textWithOpacity, 0.5),
    },
    {
      label: I18n.t('global.statusEnum.planned'),
      value: ExecutionStatus.PLANNED,
      backgroundColor: alpha(Colors.warning, 0.5),
    },
    {
      label: I18n.t('global.statusEnum.executed'),
      value: ExecutionStatus.EXECUTED,
      backgroundColor: alpha(Colors.primary, 0.5),
    },
  ];
}

export enum ExecutionTemplateMail {
  DEFAULT = 'DEFAULT',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export namespace ExecutionTemplateMail {
  export const selectItems = (): SelectItem[] => [
    {label: I18n.t('global.executionTemplateMail.DEFAULT'), value: ExecutionTemplateMail.DEFAULT},
    {label: I18n.t('global.executionTemplateMail.UPDATE'), value: ExecutionTemplateMail.UPDATE},
    {label: I18n.t('global.executionTemplateMail.DELETE'), value: ExecutionTemplateMail.DELETE},
  ];
}

export enum ExecutionFilterSource {
  FROM_DATAHUB,
  FROM_CLIENT,
}

export namespace ExecutionFilterSource {
  export const selectItems = (): SelectItem[] => [
    {label: I18n.t('global.executionFilterSource.FROM_DATAHUB'), value: ExecutionFilterSource.FROM_DATAHUB},
    {label: I18n.t('global.executionFilterSource.FROM_CLIENT'), value: ExecutionFilterSource.FROM_CLIENT},
  ];

  export const convertToParams = (value?: ExecutionFilterSource): boolean | undefined => {
    if (value === ExecutionFilterSource.FROM_CLIENT) return false;
    else if (value === ExecutionFilterSource.FROM_DATAHUB) return true;
    return undefined;
  };
}

export enum ExecutionFilterSchedule {
  WITH_SCHEDULE,
  WITHOUT_SCHEDULE,
}

export namespace ExecutionFilterSchedule {
  export const selectItems = (): SelectItem[] => [
    {label: I18n.t('global.executionFilterSchedule.WITH_SCHEDULE'), value: ExecutionFilterSchedule.WITH_SCHEDULE},
    {
      label: I18n.t('global.executionFilterSchedule.WITHOUT_SCHEDULE'),
      value: ExecutionFilterSchedule.WITHOUT_SCHEDULE,
    },
  ];

  export const convertToParams = (value?: ExecutionFilterSchedule): boolean | undefined => {
    if (value === undefined) {
      return undefined;
    }
    return value === ExecutionFilterSchedule.WITH_SCHEDULE;
  };
}

export interface ManageExecutionDistributionRule {
  clientOrganisationUnitId?: number;
  clientOrganisationUnit?: SelectItem;
  rateInPercent?: number;
}

export interface ExecutionDistributionRule {
  clientOrganisationUnit: ClientsUnit;
  rateInPercent: number;
}

export interface ExecutionComment {
  id: number;
  createdAt: Date;
  content: string;
  author?: UserLight;
}

export interface CreateBatchExecution {
  id?: number;
  status?: ExecutionStatus;
  plannedFrom?: Date;
  plannedTo?: Date;
  service?: ClientsService;
  quantity1?: number;
  quantity2?: number;
  executedAt?: Date;
}

export interface ManageExecution {
  id?: number;
  isNew?: boolean;
  name?: string;
  status?: ExecutionStatus;
  ticketSignature?: string;
  issuesCount?: number;
  commentsCount?: number;
  executionGroup?: ExecutionGroup;
  author?: UserLight;
  plannedDate?: Date;
  plannedFrom?: Date;
  plannedTo?: Date;
  service?: ExecutionDetailsService['id'];
  serviceKind?: ServiceKind['id'];
  waste?: Waste['id'];
  container?: Container['id'];
  quantity1?: number;
  organisation?: OrganisationLight;
  organisationUnit?: OrganisationUnitLight;
  recurrence?: any;
  documents?: DocumentInfo[];
  documentsToAdd?: DocumentInfo[];
  documentsToDelete?: DocumentInfo['id'][];
  executedAt?: Date;
  oldKind?: ServiceKindLight;
  updatedAt?: Date;
  quantity2?: number;
  oldService?: ExecutionDetailsService;
  generatedBySchedule?: any;
  co2?: number;
  additionalInformation?: string;
  parentExecutionId?: number;
  isFromGenericUnit?: boolean;
  distributionRules?: ManageExecutionDistributionRule[];
}

export namespace ManageExecution {
  export const newExecutionFromParent = (parentExecution: ManageExecution): ManageExecution => ({
    isNew: true,
    serviceKind: parentExecution.serviceKind,
    plannedFrom: parentExecution.plannedFrom,
    plannedTo: parentExecution.plannedTo,
    quantity1: parentExecution.quantity1 ?? 1,
    organisationUnit: parentExecution.organisationUnit,
    additionalInformation: parentExecution.additionalInformation,
  });
}

export interface ExecutionDetailsHaulingAndDestination {
  haulingOrganisationUnitName: string;
  destinationOrganisationUnitName: string;
}

export interface ExecutionDetailsService {
  id: number;
  waste: Waste;
  container: Container;
  kind: ServiceKindLight;
  canBeRequested: boolean;
  clientWasteName: string;
  supplierOrganisationName: string;
  supplierUnitName: string;
  supplierInvoicingRequired: boolean;
  clientInvoicingRequired: boolean;
  supplierOrgUnitId: number;
}

export interface ExecutionGroup {
  id: number;
  mainExecutionId: number;
}

export interface ExecutionDetails {
  id?: number;
  status: ExecutionStatus;
  createdAt: string;
  name: string;
  quantity1: number;
  quantity2: number;
  executedAt?: Date;
  ticketSignature: string;
  plannedFrom: Date;
  plannedTo: Date;
  justification: string;
  author: UserLight;
  service: ExecutionDetailsService;
  executionGroup: ExecutionGroup;
  organisationUnit: OrganisationUnitLight;
  documents: DocumentInfo[];
  generatedBySchedule?: any;
  recurrence?: any;
  commentsCount: number;
  issuesCount: number;
  updatedAt?: Date;
  canBeRequested?: boolean;
  co2: number;
  parentExecutionId?: number;
  additionalInformation?: string;
  isFromGenericUnit?: boolean;
  distributionRules?: ExecutionDistributionRule[];
}

export namespace ExecutionDetails {
  export const toManageExecution = (execution: ExecutionDetails): ManageExecution => ({
    ...execution,
    isNew: false,
    plannedDate: execution.plannedFrom,
    service: execution.service?.id,
    serviceKind: execution.service?.kind?.id,
    waste: execution.service?.waste?.id,
    container: execution.service?.container?.id,
    quantity1: execution.quantity1 || 0,
    quantity2: execution.quantity2,
    executedAt: execution.executedAt,
    updatedAt: execution.updatedAt,
    generatedBySchedule: execution.generatedBySchedule,
    oldService: execution.service,
    oldKind: {
      id: execution.service.kind.id,
      kind: execution.service.kind.kind,
      type: execution.service.kind.type,
      execUnitName2: UnitOfMeasure.UNIT,
      execUnitName1: execution.service.kind.execUnitName1,
      kindTranslateKey: execution.service.kind.kindTranslateKey,
    },
    distributionRules: execution.distributionRules?.map((dr) => ({
      clientOrganisationUnitId: dr.clientOrganisationUnit.id,
      clientOrganisationUnit: {label: dr.clientOrganisationUnit.name, value: dr.clientOrganisationUnit.id},
      rateInPercent: dr.rateInPercent,
    })),
    isFromGenericUnit: execution.isFromGenericUnit,
  });
}

export interface ExecutionsGroupedAction {
  plannedFrom?: Date;
  executedAt?: Date;
  quantity1?: number;
  quantity2?: number;
  status?: ExecutionStatus;
}

export interface ExecutionFilters {
  search: string;
  status?: ExecutionStatus;
  from?: Date;
  to?: Date;
  source?: ExecutionFilterSource;
  schedule?: ExecutionFilterSchedule;
  tableFilters: TableFilter[];
  filterDate?: ExecutionFilterDate;
}

export namespace ExecutionFilters {
  export const initTableFilters = () =>
    TableFilterType.initTableFilters([
      TableFilterType.CLIENT_ORGANISATION,
      TableFilterType.CLIENT_ORGANISATION_UNIT,
      TableFilterType.SUPPLIER_ORGANISATION,
      TableFilterType.SUPPLIER_ORGANISATION_UNIT,
      TableFilterType.WASTE_FAMILY,
      TableFilterType.WASTE,
      TableFilterType.CONTAINER,
      TableFilterType.SERVICE_KIND,
      TableFilterType.SERVICE_KIND_TYPE,
    ]);
}

export interface ExecutionsPagination extends Pagination {
  data: Execution[];
}

export interface ExecutionUI {
  id: number;
  selected: boolean;
}

export interface ExecutionStatusHistory {
  status: ExecutionStatus;
  updatedAt: Date;
  author: UserLight;
  id: number;
}

export interface ExecutionLight extends Execution {
  parentExecutionId: number;
  quantity2: number;
  theoreticalQuantity2: number;
  unit2: string;
  executionTicketSignature: string;
  plannedTo: Date;
  clientOrganisation: {
    name: string;
  };
  supplierOrganisation: {
    name: string;
  };
  additionalInformation: string;
}

export interface Execution {
  id: number;
  kindType: ServiceKindTypeEnum;
  createdAt: Date;
  plannedFrom?: Date;
  executedAt?: Date;
  type?: string;
  waste?: string;
  container: string;
  quantity1?: number;
  unit1: string;
  puBuy: string;
  puSell: string;
  name: string;
  ticketSignature: string;
  totalBuy: number;
  totalSell: number;
  status: ExecutionStatus;
  commentsCount?: number;
  issueCount?: number;
  timed?: boolean;
  approved?: boolean;
  updatedAt?: Date;
  supplierOrganisationUnit?: {
    name: string;
  };
  clientOrganisationUnit?: {
    name: string;
  };
  statusLastUpdatedAt: Date;
  recurring: boolean;
  co2: number;
  additionalInformation?: string;
  isExecutionGroup?: boolean;
}
